"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname } from "@/lib/region";
import { mergeSourced, type SourcedBlok } from "@/lib/sourced-blok";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type Asset = { filename?: string; alt?: string };

type ImageLinkCard = SbBlokData & {
  image?: Asset;
  title?: string;
  description?: string;
  link_label?: string;
  link?: StoryblokLink;
};

type GlobalCardsWithImageAndLinkFields = {
  title?: string;
  cards?: ImageLinkCard[];
  bg_color?: string;
  card_bg_color?: string;
};

type GlobalCardsWithImageAndLinkBlok = SbBlokData &
  SourcedBlok<GlobalCardsWithImageAndLinkFields>;

const FIELDS: (keyof GlobalCardsWithImageAndLinkFields)[] = [
  "title",
  "cards",
  "bg_color",
  "card_bg_color",
];

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function GlobalCardsWithImageAndLink({
  blok: rawBlok,
}: {
  blok: GlobalCardsWithImageAndLinkBlok;
}) {
  const blok = mergeSourced(rawBlok, FIELDS);
  const region = regionFromPathname(usePathname());
  const { bg, onDark } = resolveBg(blok.bg_color, "white");
  const card = resolveBg(blok.card_bg_color, "purple-00");
  const cards = blok.cards ?? [];
  const fewCards = cards.length < 4;

  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const update = () => {
      const rect = scroller.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      let closest = 0;
      let minDist = Infinity;
      cardRefs.current.forEach((el, i) => {
        if (!el) return;
        const cRect = el.getBoundingClientRect();
        const cCenter = cRect.left + cRect.width / 2;
        const dist = Math.abs(cCenter - center);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (scroller.scrollLeft <= 1) closest = 0;
      else if (scroller.scrollLeft >= maxScroll - 1) closest = cards.length - 1;
      setActiveIndex(closest);
      setIsScrollable(scroller.scrollWidth > scroller.clientWidth + 4);
    };
    update();
    scroller.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      scroller.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [cards.length]);

  if (cards.length === 0) return null;

  return (
    <section
      {...storyblokEditable(blok)}
      className={`${bg} ${onDark ? "text-white" : "text-black"}`}
    >
      <div className="pt-[30px] pb-[40px] lg:pt-[47px] lg:pb-[60px]">
        {blok.title && (
          <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)]">
            <h2 className="text-[clamp(2.25rem,4vw,3rem)] text-purple-05 text-center leading-[1.08] font-bold">
              {blok.title}
            </h2>
          </div>
        )}

        <div
          ref={scrollerRef}
          className={`scrollbar-none mt-[clamp(2rem,4vw,3.75rem)] overflow-x-auto overflow-y-clip py-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
            fewCards ? "lg:flex lg:justify-center" : ""
          }`}
        >
          <ul
            className={`flex w-max gap-5 pr-5 pl-[clamp(1.25rem,4vw,2.5rem)] ${
              fewCards
                ? "lg:px-0"
                : "lg:pl-[max(2.5rem,calc((100vw-1280px)/2+2.5rem))]"
            }`}
          >
            {cards.map((item, i) => {
              const link = resolveLink(item.link, region);
              const clickable = link.href !== "#";
              const outline = onDark
                ? "focus-visible:outline-white"
                : "focus-visible:outline-purple-03";

              const inner = (
                <>
                  {item.image?.filename && (
                    <div className="relative aspect-590/312 w-full bg-purple-01">
                      <Image
                        src={item.image.filename}
                        alt={item.image.alt || ""}
                        fill
                        sizes="285px"
                        className="object-cover"
                      />
                    </div>
                  )}

                  <div
                    className={`flex flex-1 flex-col p-5 lg:p-6 ${card.bg} ${
                      card.onDark ? "text-white" : "text-black"
                    }`}
                  >
                    {item.title && (
                      <h3 className="text-h6 leading-[1.08] font-bold">
                        {item.title}
                      </h3>
                    )}
                    {item.description && (
                      <p
                        className={`mt-2 text-body leading-[1.56] ${
                          card.onDark ? "text-white/85" : "text-[#595959]"
                        }`}
                      >
                        {item.description}
                      </p>
                    )}
                    {item.link_label && (
                      <span
                        className={`mt-auto inline-flex items-center gap-2 pt-4 text-body font-bold group-hover:underline ${
                          card.onDark ? "text-white" : "text-purple-04"
                        }`}
                      >
                        {item.link_label}
                        <ArrowIcon />
                      </span>
                    )}
                  </div>
                </>
              );

              const cardClass =
                "flex h-full flex-col overflow-hidden rounded-[16px] lg:rounded-[20px]";

              return (
                <li
                  key={item._uid as string}
                  ref={(el) => {
                    cardRefs.current[i] = el;
                  }}
                  {...storyblokEditable(item)}
                  className="w-[285px] shrink-0"
                >
                  {clickable ? (
                    <a
                      href={link.href}
                      {...(link.target ? { target: link.target } : {})}
                      className={`group ${cardClass} focus-visible:outline-2 focus-visible:outline-offset-2 ${outline}`}
                    >
                      {inner}
                    </a>
                  ) : (
                    <div className={cardClass}>{inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {isScrollable && cards.length > 1 && (
          <div className="mt-6 flex justify-center">
            <div className="flex items-center">
              {cards.map((item, i) => (
                <button
                  key={item._uid as string}
                  type="button"
                  onClick={() => {
                    cardRefs.current[i]?.scrollIntoView({
                      behavior: "smooth",
                      inline: "center",
                      block: "nearest",
                    });
                  }}
                  aria-label={`Go to card ${i + 1}`}
                  className="flex h-6 min-w-6 cursor-pointer items-center justify-center"
                >
                  <span
                    aria-hidden
                    className={`block h-1.5 rounded-full transition-all duration-300 ${
                      activeIndex === i ? "w-8 bg-purple-03" : "w-2 bg-purple-01"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
